import React, { ReactNode, useState } from 'react';
import { css } from '@emotion/react';
import ChevronDown from '@atlaskit/icon/utility/chevron-down';
import ChevronUp from '@atlaskit/icon/utility/chevron-up';
import { token } from '@atlaskit/tokens';

interface NavBarExpandableItemProps {
  headerContent: ReactNode;
  expandedHeaderContent?: ReactNode;
  // if there's no body, then don't show accordion
  bodyContent?: ReactNode;
  /** (optional) additional function to be called when the user clicks on the item to expand it, called immediately
   * after the menu is opened/closed (can be used for sending analytics events)
   */
  onExpandClick?: (isOpen: boolean) => void;
  /** (optional) True if this link item is nested under another link item, applies only to mobile/tablet view */
  isNested?: boolean;
}

export const NavBarExpandableItem: React.FC<NavBarExpandableItemProps> = ({
  headerContent,
  expandedHeaderContent,
  bodyContent,
  onExpandClick,
  isNested = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const onExpandClickHandler = (): void => {
    setIsOpen(!isOpen);
    if (onExpandClick) {
      onExpandClick(isOpen);
    }
  };
  return (
    <div css={containerStyles} role="group">
      {bodyContent ? (
        <>
          <div
            css={[headerStyles, isOpen && headerSelectedStyles, isNested && nestedHeaderStyles]}
            role="treeitem"
            onClick={onExpandClickHandler}
          >
            {isOpen && expandedHeaderContent ? expandedHeaderContent : headerContent}
            <span css={isOpen && chevronSelectedStyles}>
              {isOpen ? <ChevronUp label={'Collapse item'} /> : <ChevronDown label={'Expand item'} />}
            </span>
          </div>
          {isOpen && (
            <ul css={[bodyContentContainerStyles, isNested && nestedBodyContentContainerStyles]}>{bodyContent}</ul>
          )}
        </>
      ) : (
        <div css={headerStyles} role="treeitem">
          {headerContent}
        </div>
      )}
    </div>
  );
};

const containerStyles = css({
  display: 'flex',
  fontSize: '1rem',
  alignItems: 'flex-start',
  flexDirection: 'column',
  width: '100%',
});

const headerStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  height: '32px',
  width: '100%',
  padding: '0 24px',
});

// when selected, header text color should appear as selected color (apply to different elements, since headerContent can vary)
const headerSelectedStyles = css({
  color: token('color.text.selected'),
  a: {
    color: token('color.text.selected'),
  },
  span: {
    color: token('color.text.selected'),
  },
});

const nestedHeaderStyles = css({
  padding: '0 40px', // 24px + 16px (for nested item, add 16px to base padding - headerStyles)
});

const bodyContentContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: '8px',
  paddingTop: '8px',
  paddingLeft: '0', // remove default padding from ul
  margin: 0,
  listStyle: 'none',
});

const nestedBodyContentContainerStyles = css({
  li: {
    paddingLeft: '56px', // 40px + 16px (add to padding of MenuItem - menuItemContainerStyles)
  },
});

const chevronSelectedStyles = css({
  svg: {
    color: token('color.text.selected'),
  },
});
